import "./styles/booth.css"

import { useState, useEffect } from "preact/hooks"
import { Arch, archCss } from "./booth/Arch"
import { chatCss, ChatLogo } from "./booth/ChatLogo"
import { Pointer } from "./booth/Pointer"
import { Calendar } from "./booth/Calendar"
import { Links } from "src/utils/fw"
import { BoothFollowUp } from "./booth/BoothFollowUp"
import VideoModal, { defaultModal, largeVideoModalProps } from "./VideoModal"
import { DownloadIcon } from "./booth/DownloadIcon"
import PlayButton from "./booth/PlayButton"

const IMGURL= "https://byob.tkevents.ca/front/assets"
const APIURL = "https://byob.tkevents.ca"

const imageUrl = (filename) => {
    return filename.startsWith("http") ? filename : `${IMGURL}/${filename}.png`
}

// Add aspect ratio based on width (CSS LOL)
export const Booth = ({boothId, position = {top, left, width}, onChatOpen = (() => {}), onLinkClick = null}) => {

    const {top = 0, left = 0, width = "100%"} = position 
    const [booth, setBooth] = useState(null)

    if (booth === null) {
        getBooth(boothId).then(it => {
            setBooth(it)
        }).catch(() => setBooth(false))
        return <></>
    }

    if (booth === false) return <></>

    const layers = []
    const links = []

    // Graphic 1
    if (booth.permissions && !booth.permissions.background) {
        const bg = booth.permissions.backgroundDefault
        layers.push(<div style={{ background: bg ? bg : "#FFF" }} className="graphic1" />)
    } else 
        layers.push(<div style={{ 
            background: booth.graphic1URL ? `URL('${imageUrl(booth.graphic1URL)}')` : (booth.colorleft || "none"),
            backgroundColor: "white",
            backgroundSize: "cover"
        }} className="graphic1" />)

    // Arch layout
    layers.push(<img src="https://byob.tkevents.ca/front/assets/archBooth.png" className="fill"/>)

    // Graphic 2
    layers.push(<div style={{ 
        backgroundColor: booth.color1, 
        backgroundImage: booth.graphic2URL ? `URL('${imageUrl(booth.graphic2URL)}')` : "none",
        backgroundSize: "cover"
    }} className="graphic2" />)

    // Arch
    layers.push(<div className="fill" style={archCss}><Arch color={booth.color1}/></div>)

    // Chat bubble
    if (booth.chatColor) {
        links.push({
            width: "8.8%",
            height: "16.1%",
            left: "15.4%",
            top: "23.8%",
            label: "Chat",
            onClick: onChatOpen || (() => {})
        })
        layers.push(<div style={chatCss}><ChatLogo color={booth.chatColor || booth.color1} /></div>)
    }

    // Company logo
    if (booth.companyLogo) {
        links.push({
            width: "15.1%", height: "13%", left: "59.9%", top: "22.6%",
            label: `Company - ${booth.name}`,
            href: booth.companyLink,
            target: "_blank",
            onClick() {
                onLinkClick?.call(null, "company_link")
            }
        })

        layers.push(<div
            style={{ position: "absolute", width: "15.1%", left: "59.9%", top: "22.6%", aspectRatio: "2/1", }}>
            <img src={booth.companyLogo} style={{ width: "100%", height: "100%" }} />
        </div>)
    }

    // Video
    if (booth.videoURL) {
        try {
            const videoURL = new URL(booth.videoURL)
            videoURL.searchParams.append("autoplay", "1")
            videoURL.searchParams.append("muted", "0")

            links.push({
                width: "14.2%", height: "18.1%", left: "60.4%", top: "36.8%",
                label: `Video - ${booth.name}`,
                modalOverrides: largeVideoModalProps,
                modal: <VideoModal url={videoURL.toString()}/>,
                onClick() {
                    onLinkClick?.call(null, "video_popup")
                }
            })
        } catch {
            links.push({
                width: "14.2%", height: "18.1%", left: "60.4%", top: "36.8%",
                label: `Video - ${booth.name}`,
                modalOverrides: largeVideoModalProps,
                modal: <VideoModal url={booth.videoURL}/>,
                onClick() {
                    onLinkClick?.call(null, "video_popup")
                }
            })
        }

        layers.push(<Layer filename="screen1" />)
        if (booth.videoThumb) layers.push(<div className="videoThumbHandler">
            <img style={{ filter: "brightness(0.6)", width: "100%", height: "100%" }} src={ booth.videoThumb }/>
            <span className="videoThumbIcon"> <PlayButton /> </span>
        </div>)
    }

    // Plant
    if(booth.plantID) booth.plantID.forEach(plant => {layers.push(<Layer filename={imageUrl(`FURNITURE/plant${plant}`)} />)})

    // People
    if (booth.avatarID) layers.push(<img src={imageUrl(`PEOPLE/${booth.avatarID}`)} style={{ position: "absolute", left: "21%", bottom: "25%", height: "39%" }} />)

    if (booth.meetingURL || booth.followup || booth.hasCounter || booth.mailTo) {

        const modalOverrides = {...defaultModal, style:{...defaultModal.style, background:booth.counterColor, width:"25%", height:"25%", padding:"1em 1em"}}
        const row = {width: "16.8%", height: "21.1%", left: "16.4%", top: "57.8%"}

        let followUp = {
            ...row, 
            label: `Follow up - ${booth.name}`,
            onClick() { onLinkClick?.call(null, "follow_up")}
        }

        if (booth.mailTo) {
            followUp = {
                ...followUp, 
                target: "_blank",
                isExternal: true,
                href: `mailto:${booth.mailTo}`
            }
        } else if(booth.followup) {
            followUp = {
                ...followUp, 
                modalOverrides,
                modal: <BoothFollowUp apiURL={APIURL} boothId={booth.uuid} color={booth.counterLinkColor} formItems={booth.followup} />
            }
        } else {
            followUp = false
        }

        if (booth.meetingURL && followUp) {
            links.push({...followUp, width: "8.3%"})
            links.push({...row, width: "7.6%", left: "25.4%", label: `Book a Meeting - ${booth.name}`, href: booth.meetingURL, onClick() { onLinkClick?.call(null, "book_meeting")}})
        } else if (booth.meetingURL || followUp) {
            followUp ? links.push(followUp) : null
            booth.meetingURL ? links.push({...row, label: `Book a Meeting - ${booth.name}`, href: booth.meetingURL, target: '_blank', onClick() { onLinkClick?.call(null, "book_meeting")}}) : null
        }


        layers.push(<Layer filename="counterMeeting" />)
        layers.push(<div className="counter counter-row m-center x-center" style={{ backgroundColor: booth.counterColor, overflow: "hidden" }}>
            {!booth.meetingURL && !booth.followup && booth.logoURL && <img className="fill" src={booth.logoURL} />}
            {booth.followup && <Pointer color={booth.counterLinkColor} />}
            {booth.meetingURL && <Calendar color={booth.counterLinkColor} />}
        </div>)
    }

    // Furniture (tables and chairs)
    if (booth.furnitureID) layers.push(<Layer filename={imageUrl(`FURNITURE/chairsTable${booth.furnitureID}`)} />)

    if (booth.downloadURL) {
        links.push({
            width: "6.8%", height: "31.1%", left: "75.4%", top: "47.8%",
            label: `Download File - ${booth.name}`,
            isExternal: true,
            target: "_blank",
            href: booth.downloadURL,
            onClick() { onLinkClick?.call(null, "download_file")}
        })

        if (booth.downloadThumbnailURL) {
            layers.push(<Layer filename="brochureHolder2" />)
            layers.push(<div className="downloadThumbnail" style={{ backgroundImage: `url(${booth.downloadThumbnailURL})` }}/>)
        } else layers.push(<Layer filename="brochureHolder1" />)
        layers.push(<div className="downloadIcon"><DownloadIcon color={booth.downloadColor || booth.counterLinkColor}/></div>)
    }

    return <div class="byob" style={{position:"absolute", top, left, width: "100%", maxWidth: width, overflow: "hidden", aspectRatio:"2 / 1"}}>
        <div className="viewer splitWall" style={{width: "144%", marginLeft: "-23%", marginTop: "-10%"}}>
            <div> { layers } </div>
        </div>
        <ClickableZone id={booth.name} links={links} />
    </div>
}

const ClickableZone = ({links = [], id = ""}) => <div style={{position: "absolute", top: 0, width: "144%", marginLeft: "-23%", marginTop: "-10%", aspectRatio: "2 / 1"}}>
    <Links links={links} roomName={`booth-${id}`} />
</div>

const Layer = ({
    filename = "",
    style= ""
}) => <img src={imageUrl(filename)} className="fill" style={style}/>

export const getBooths = async (id) => {
    const res = await fetch(`${APIURL}/api/booths/${id}/all`);
    return await res.json();
}

const getBooth = async (boothId) => {
    const res = await fetch(`${APIURL}/api/booths/${boothId}/public`);
    return await res.json();
}
